/**
 * The game screen (UX-SPEC §3–§5), laid out per breakpoint (see useLayout):
 *
 *  - desktop ≥ 1180: [player cards · presence · bag · menu] [board · rack · actions] [preview · history]
 *  - tablet 760–1179 wide and ≥ 560 tall: chip top bar, then
 *    [board · rack · actions] [preview · history] (one column in portrait)
 *  - mobile (narrower or shorter): chip top bar, zoomable board, preview strip,
 *    rack, actions; history and the move's full breakdown (tap the strip) in
 *    bottom sheets. A phone in landscape puts the board on the left and
 *    everything else beside it.
 *
 * It wires the window-level keyboard shortcuts and the drag controller, and
 * hosts the game's overlays (letter picker, exchange, confirms, game over,
 * coach-marks, celebrations).
 */
import { useCallback, useEffect, useRef, type ReactNode } from 'react';
import { LayoutGroup, MotionConfig } from 'motion/react';
import clsx from 'clsx';
import { CENTER, isBoardEmpty, type Coord, type GameView, type RoomView, type Seat, type Tile } from '@wordloom/engine';
import { visibleRackIds } from '../game/draft';
import { opponentSeat, seatName } from '../game/events';
import { handleGlobalKey, installDragHandlers } from '../game/interactions';
import { dragController } from '../game/drag';
import type { ActionContext } from '../game/selectors';
import { centreOn, useFollowPlacement } from '../hooks/useBoardZoom';
import { useLayout, useReducedMotion, type LayoutMode } from '../hooks/useMediaQuery';
import { usePreview } from '../hooks/usePreview';
import { useDraftStore } from '../state/draftStore';
import { useRoomStore } from '../state/roomStore';
import { useUiStore } from '../state/uiStore';
import { ActionBar } from './ActionBar';
import { BagCounter } from './BagCounter';
import { Board } from './Board';
import { Celebration } from './Celebration';
import { CoachMarks } from './CoachMarks';
import { ExchangeDialog } from './ExchangeDialog';
import { GameDialogs, GameMenuCard, GameMenuSheet } from './GameMenu';
import { GameOverBar, GameOverDialog } from './GameOver';
import { IconHistory, IconMenu, IconZoomIn, IconZoomOut } from './Icons';
import { LetterPicker } from './LetterPicker';
import { Modal } from './Modal';
import { MoveHistory, MoveHistoryList } from './MoveHistory';
import { MovePreview, PreviewAnnouncer, PreviewStrip } from './MovePreview';
import { PlayerCard } from './PlayerCard';
import { PresenceBanner } from './PresenceBanner';
import { Rack, tileLabel } from './Rack';
import { TileFace } from './Tile';
import { Tooltip } from './Tooltip';
import { TurnBanner } from './TurnBanner';
import { VersionPill } from './VersionPill';
import './GameScreen.css';

interface GameScreenProps {
  view: RoomView;
  game: GameView;
}

// ---------------------------------------------------------------------------
// Mobile board zoom
// ---------------------------------------------------------------------------

/**
 * The board plus, on phones, the zoom wrapper. `strip` (the phone's preview
 * strip) is rendered under the board next to the zoom button, so the button
 * never covers (and swallows taps on) the corner squares. The strip row is a
 * sibling of `.board-area`, not a child, so the phone landscape layout can
 * put it in the side column while the board fills the height.
 */
function BoardArea({
  view,
  game,
  layout,
  preview,
  strip,
}: GameScreenProps & { layout: LayoutMode; preview: ReturnType<typeof usePreview>; strip?: ReactNode }) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const zoomed = useUiStore((s) => s.boardZoomed);
  const setZoomed = useUiStore((s) => s.setBoardZoomed);
  const placements = useDraftStore((s) => s.placements);
  const reduced = useReducedMotion();
  const mobile = layout === 'mobile';
  const names: [string, string] = [seatName(view, 0), seatName(view, 1)];

  // The zoom only exists on phones.
  useEffect(() => {
    if (!mobile && zoomed) setZoomed(false);
  }, [mobile, zoomed, setZoomed]);

  // Edge auto-scroll while dragging over the zoomed board.
  useEffect(() => {
    dragController.registerScroller(mobile ? scrollRef.current : null);
    return () => dragController.registerScroller(null);
  }, [mobile]);

  const focusPoint = useCallback((): Coord => {
    const last = useDraftStore.getState().placements.at(-1);
    return last ?? useDraftStore.getState().cursor ?? { row: CENTER, col: CENTER };
  }, []);

  const toggleZoom = useCallback(
    (at?: Coord) => {
      const next = !useUiStore.getState().boardZoomed;
      setZoomed(next);
      if (next) {
        // Wait for the enlarged layout before scrolling to the square.
        requestAnimationFrame(() => {
          if (scrollRef.current) centreOn(scrollRef.current, at ?? focusPoint(), false);
        });
      }
    },
    [setZoomed, focusPoint],
  );

  // Keep the newest placed tile in view while zoomed (only when it nears the edge).
  useFollowPlacement(scrollRef, zoomed, placements.at(-1), !reduced);

  const board = (
    <Board
      game={game}
      you={view.you}
      names={names}
      preview={preview}
      onDoubleTap={mobile ? (row, col) => toggleZoom({ row, col }) : undefined}
    />
  );

  return (
    <>
      <div className="board-area">
        <TurnBanner />
        {mobile ? (
          <div className={clsx('board-scroll', zoomed && 'is-zoomed')} ref={scrollRef}>
            <div className="board-scroll__inner">{board}</div>
          </div>
        ) : (
          board
        )}
      </div>
      {mobile && (
        <div className="board-strip">
          {strip}
          {/* A toggle keeps one name; aria-pressed carries the state. */}
          <Tooltip content={zoomed ? 'Zoom out' : 'Zoom in'}>
            <button
              type="button"
              className="btn btn--icon btn--sm board-zoom"
              aria-pressed={zoomed}
              aria-label="Zoom board"
              onClick={() => toggleZoom()}
            >
              {zoomed ? <IconZoomOut /> : <IconZoomIn />}
            </button>
          </Tooltip>
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Layout pieces
// ---------------------------------------------------------------------------

function useActionContext(view: RoomView, game: GameView, preview: ReturnType<typeof usePreview>): ActionContext {
  const placements = useDraftStore((s) => s.placements);
  const rackOrder = useDraftStore((s) => s.rackOrder);
  const pending = useDraftStore((s) => s.pending);
  const connection = useRoomStore((s) => s.connection);
  const joined = useRoomStore((s) => s.session?.joined ?? false);
  return {
    gameActive: game.status === 'playing',
    isMyTurn: game.status === 'playing' && game.currentSeat === view.you,
    online: connection === 'connected' && joined,
    pending,
    draftCount: placements.length,
    rackVisibleCount: visibleRackIds(rackOrder, placements).length,
    preview,
    bagCount: game.bagCount,
    opponentName: seatName(view, opponentSeat(view)),
  };
}

function Players({ view, game, variant }: GameScreenProps & { variant: 'card' | 'chip' }) {
  const order: Seat[] = [view.you, opponentSeat(view)];
  return (
    <>
      {order.map((seat) => (
        <PlayerCard
          key={seat}
          seat={seat}
          seatView={view.seats[seat] ?? null}
          player={game.players[seat] ?? null}
          isYou={seat === view.you}
          game={game}
          variant={variant}
        />
      ))}
    </>
  );
}

function TopBar({ view, game, children }: GameScreenProps & { children?: ReactNode }) {
  const dialog = useUiStore((s) => s.dialog);
  const openDialog = useUiStore((s) => s.openDialog);
  return (
    <header className="game-topbar">
      <div className="game-topbar__players">
        <Players view={view} game={game} variant="chip" />
      </div>
      <div className="game-topbar__tools">
        <BagCounter count={game.bagCount} variant="chip" />
        {children}
        <Tooltip content="Menu">
          <button
            type="button"
            className="btn btn--icon btn--ghost game-topbar__btn"
            aria-label="Game menu"
            aria-haspopup="dialog"
            aria-expanded={dialog === 'menu'}
            data-menu-trigger=""
            onClick={() => openDialog('menu')}
          >
            <IconMenu />
          </button>
        </Tooltip>
      </div>
    </header>
  );
}

/**
 * After the game: the tiles you were left holding (they count against you),
 * read-only — no buttons, nothing to drag.
 */
function FinalRack({ rack }: { rack: readonly Tile[] }) {
  if (rack.length === 0) return null;
  return (
    <div className="rack rack--final" role="group" aria-labelledby="final-rack-caption">
      <p id="final-rack-caption" className="rack__caption">
        Tiles left
      </p>
      <ul className="rack__tray">
        {rack.map((tile) => (
          <li key={tile.id} className="rack__slot">
            <span className="tile rack__tile" data-blank={tile.blank || undefined}>
              <TileFace letter={tile.letter} value={tile.value} blank={tile.blank} />
            </span>
            <span className="sr-only">{tileLabel(tile)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function TurnControls({ view, game, ctx }: GameScreenProps & { ctx: ActionContext }) {
  if (game.status === 'finished') return <GameOverBar view={view} game={game} />;
  return <ActionBar ctx={ctx} consecutivePasses={game.consecutivePasses} passLimit={game.passLimit} />;
}

// ---------------------------------------------------------------------------
// Screen
// ---------------------------------------------------------------------------

export function GameScreen({ view, game }: GameScreenProps) {
  const layout = useLayout();
  const placements = useDraftStore((s) => s.placements);
  const pending = useDraftStore((s) => s.pending);
  const dialog = useUiStore((s) => s.dialog);
  const openDialog = useUiStore((s) => s.openDialog);
  const closeDialog = useUiStore((s) => s.closeDialog);
  const preview = usePreview(game, placements);
  const ctx = useActionContext(view, game, preview);
  const playing = game.status === 'playing';
  const firstMove = isBoardEmpty(game.board);
  const names: [string, string] = [seatName(view, 0), seatName(view, 1)];

  useEffect(() => installDragHandlers(), []);

  useEffect(() => {
    window.addEventListener('keydown', handleGlobalKey);
    return () => window.removeEventListener('keydown', handleGlobalKey);
  }, []);

  // First game: coach-marks (skipped once dismissed or after the first play).
  useEffect(() => {
    if (playing) useUiStore.getState().startOnboarding();
  }, [playing]);

  // The history and move sheets only exist on mobile (the move sheet only
  // while playing) and the menu sheet below desktop: after a resize, a
  // rotation or the game ending, close the one that is no longer rendered, or
  // it would stay "open" invisibly and swallow the keyboard shortcuts.
  useEffect(() => {
    const gone =
      (layout !== 'mobile' && dialog === 'history') ||
      ((layout !== 'mobile' || !playing) && dialog === 'preview') ||
      (layout === 'desktop' && dialog === 'menu');
    if (gone) closeDialog();
  }, [layout, dialog, playing, closeDialog]);

  const rack = playing ? <Rack rack={game.rack} interactive={!pending} /> : <FinalRack rack={game.rack} />;
  const controls = <TurnControls view={view} game={game} ctx={ctx} />;
  const history = <MoveHistory history={game.history} names={names} you={view.you} />;
  // The live preview only means something while the game runs.
  const movePreview = playing ? <MovePreview preview={preview} firstMove={firstMove} isMyTurn={ctx.isMyTurn} /> : null;

  let body: ReactNode;
  if (layout === 'desktop') {
    body = (
      <div className="game game--desktop">
        <aside className="game__left" aria-label="Players and settings">
          <Players view={view} game={game} variant="card" />
          {/* Beside the player cards, not above the board: the board (and with
              it the action bar's labels) must not shrink and regrow when the
              opponent drops and comes back. */}
          <PresenceBanner view={view} />
          <BagCounter count={game.bagCount} />
          <GameMenuCard view={view} game={game} />
          <VersionPill className="game__version" />
        </aside>
        <section className="game__centre" aria-label="Board">
          <BoardArea view={view} game={game} layout={layout} preview={preview} />
          <div className="game__controls">
            {rack}
            {controls}
          </div>
        </section>
        <aside className="game__right" aria-label="Move preview and history">
          {movePreview}
          {history}
        </aside>
      </div>
    );
  } else if (layout === 'tablet') {
    body = (
      <div className="game game--tablet">
        <TopBar view={view} game={game} />
        <section className="game__centre" aria-label="Board">
          <PresenceBanner view={view} />
          <BoardArea view={view} game={game} layout={layout} preview={preview} />
          <div className="game__controls">
            {rack}
            {controls}
          </div>
        </section>
        <aside className="game__right" aria-label="Move preview and history">
          {movePreview}
          {history}
        </aside>
        <footer className="app-footer game__footer">
          <VersionPill />
        </footer>
      </div>
    );
  } else {
    body = (
      <div className="game game--mobile">
        <TopBar view={view} game={game}>
          <Tooltip content="History">
            <button
              type="button"
              className="btn btn--icon btn--ghost game-topbar__btn"
              aria-label={`Move history, ${game.history.length} ${game.history.length === 1 ? 'move' : 'moves'}`}
              aria-haspopup="dialog"
              aria-expanded={dialog === 'history'}
              onClick={() => openDialog('history')}
            >
              <IconHistory />
            </button>
          </Tooltip>
        </TopBar>
        <PresenceBanner view={view} />
        <BoardArea
          view={view}
          game={game}
          layout={layout}
          preview={preview}
          strip={playing ? <PreviewStrip preview={preview} firstMove={firstMove} /> : null}
        />
        <div className="game__controls">
          {rack}
          {controls}
        </div>
        <footer className="app-footer game__footer">
          <VersionPill />
        </footer>
        <Modal open={dialog === 'history'} onClose={closeDialog} title="History" variant="sheet" className="history-sheet">
          <MoveHistoryList history={game.history} names={names} you={view.you} />
        </Modal>
        {playing && (
          <Modal open={dialog === 'preview'} onClose={closeDialog} title="This move" variant="sheet" className="preview-sheet">
            <MovePreview preview={preview} firstMove={firstMove} isMyTurn={ctx.isMyTurn} detailed />
          </Modal>
        )}
      </div>
    );
  }

  return (
    <MotionConfig reducedMotion="user">
      <LayoutGroup>
        <main id="main" className="game-main">
          <h1 className="sr-only">Wordloom — room {view.code}</h1>
          {body}
        </main>
        {layout !== 'desktop' && <GameMenuSheet view={view} game={game} />}
        <GameDialogs view={view} game={game} />
        <ExchangeDialog game={game} />
        {playing && <LetterPicker />}
        {playing && <CoachMarks board={game.board} />}
        {game.status === 'finished' && <GameOverDialog view={view} game={game} />}
        <PreviewAnnouncer preview={preview} />
        <Celebration />
      </LayoutGroup>
    </MotionConfig>
  );
}
